/**
 * Frontend display overrides for bundle data from the catalog API.
 *
 * Keeps the backend catalog source-of-truth while allowing us to adjust
 * user-facing copy (e.g. product rebrands) and inject cross-product feature
 * rows (e.g. surfacing JotTransfer inclusion on tier cards) without a
 * round-trip through the backend or a Stripe product edit.
 */

import { useMemo } from 'react';

import type { BundleWithStatus, Feature } from './types';

const EHR_NOTE_COPY_CAPABILITY = 'ehr_note_copy';

const EHR_ADDON_DISPLAY = {
  name: 'JotTransfer',
  description: 'Chrome extension for transferring notes directly into your EHR system',
};

/**
 * Rewrite a backend feature-row name to its JotTransfer equivalent.
 * Matches variations like "Unlimited EHR note copy", "EHR note copy", etc.
 */
function renameEhrAddonFeature(name: string): string {
  if (/ehr note cop(y|ies)/i.test(name)) {
    return /unlimited/i.test(name) ? 'Unlimited JotTransfers' : 'JotTransfers';
  }
  return name;
}

const JOTTRANSFER_TIER_FEATURE: Feature = {
  name: 'Unlimited JotTransfers',
  included: true,
};

function bundleGrantsEhrNoteCopy(bundle: BundleWithStatus): boolean {
  return bundle.grants?.some(g => g.capability_name === EHR_NOTE_COPY_CAPABILITY) ?? false;
}

function bundleGrantsUnlimitedEhrNoteCopy(bundle: BundleWithStatus): boolean {
  return (
    bundle.grants?.some(g => g.capability_name === EHR_NOTE_COPY_CAPABILITY && g.quota === null) ??
    false
  );
}

/**
 * Return a display-adjusted copy of a bundle.
 *
 * - Rebrands the EHR Extension add-on to "JotTransfer" in the name, description,
 *   and feature list.
 * - Appends "Unlimited JotTransfers" to tier bundles that grant unlimited
 *   ehr_note_copy usage.
 */
export function getDisplayBundle(bundle: BundleWithStatus): BundleWithStatus {
  let result = bundle;

  const isEhrAddon = bundle.bundle_type === 'add_on' && bundleGrantsEhrNoteCopy(bundle);

  if (isEhrAddon) {
    const renamedFeatures = (bundle.marketing_features ?? []).map(f => ({
      ...f,
      name: renameEhrAddonFeature(f.name),
    }));
    result = {
      ...bundle,
      name: EHR_ADDON_DISPLAY.name,
      description: EHR_ADDON_DISPLAY.description,
      marketing_features: renamedFeatures,
    };
  }

  const shouldInjectJotTransferOnTier =
    bundle.bundle_type === 'tier' && bundleGrantsUnlimitedEhrNoteCopy(bundle);

  if (shouldInjectJotTransferOnTier) {
    const existing = result.marketing_features ?? [];
    const alreadyPresent = existing.some(f => /jottransfer/i.test(f.name));
    if (!alreadyPresent) {
      result = {
        ...result,
        marketing_features: [...existing, JOTTRANSFER_TIER_FEATURE],
      };
    }
  }

  return result;
}

/**
 * Hook wrapper for display-adjusting a single bundle.
 */
export function useDisplayBundle(
  bundle: BundleWithStatus | null | undefined,
): BundleWithStatus | null {
  return useMemo(() => (bundle ? getDisplayBundle(bundle) : null), [bundle]);
}

/**
 * Hook wrapper for display-adjusting a list of bundles.
 */
export function useDisplayBundles(bundles: BundleWithStatus[]): BundleWithStatus[] {
  return useMemo(() => bundles.map(getDisplayBundle), [bundles]);
}
